/**
 * CSV importer for product cost prices.
 *
 * Accepts a CSV with header line and at least one of: `barcode` or
 * `product_id`. Required column: `cost_price`.
 *
 * Example file (UTF-8, comma OR semicolon separated):
 *   barcode,cost_price
 *   3700000000001,12.50
 *   3700000000018,9.80
 *
 * The file is parsed client-side and sent as a JSON list to
 * PUT /api/admin/products/cost-prices.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Download, FileSpreadsheet, AlertCircle, Check } from 'lucide-react';
import { extractErrorMessage } from '../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function parseCsv(text) {
  // Split CRLF, LF or just LF
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  if (!lines.length) return [];
  // Detect delimiter (comma vs semicolon)
  const delim = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ';' : ',';
  const headers = lines[0].split(delim).map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const cells = line.split(delim).map(c => c.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] ?? ''; });
    return row;
  });
}

export const CostPriceImporter = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    const text = await f.text();
    const rows = parseCsv(text).filter(r =>
      (r.barcode || r.product_id) && r.cost_price !== ''
    ).map(r => ({
      barcode: r.barcode ? String(r.barcode).trim() : undefined,
      product_id: r.product_id ? String(r.product_id).trim() : undefined,
      cost_price: parseFloat(String(r.cost_price).replace(',', '.')),
    })).filter(r => !isNaN(r.cost_price));
    setPreview(rows);
    setResult(null);
  };

  const submit = async () => {
    if (!preview.length) {
      toast.error('Aucune ligne valide dans le fichier');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/products/cost-prices`, {
        updates: preview,
      });
      setResult(res.data);
      toast.success(`${res.data.updated} prix d'achat mis à jour`);
      if (onImported) onImported(res.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Échec de l'import"));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = () => {
    const content = 'barcode,cost_price\n3700000000001,12.50\n3700000000018,9.80\n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele-prix-achat.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="cost-import-modal">
      <div className="bg-[color:var(--color-surface)] border border-white/10 w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h3 className="text-white text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Importer les prix d'achat
            </h3>
            <p className="text-zinc-500 text-xs mt-1">CSV avec colonnes <code className="text-[color:var(--color-primary)]">barcode</code> (ou <code className="text-[color:var(--color-primary)]">product_id</code>) et <code className="text-[color:var(--color-primary)]">cost_price</code></p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Template download */}
          <button type="button" onClick={downloadTemplate}
            className="flex items-center gap-2 text-xs text-[color:var(--color-primary)] hover:underline"
            data-testid="download-template">
            <Download className="w-3 h-3" />
            Télécharger un modèle CSV
          </button>

          {/* File input */}
          <label className="block border-2 border-dashed border-white/10 hover:border-[color:rgb(var(--primary-rgb)/0.30)] p-8 text-center cursor-pointer transition-colors">
            <input type="file" accept=".csv,text/csv" onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden" data-testid="cost-csv-input" />
            <FileSpreadsheet className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
            <p className="text-zinc-400 text-sm">
              {file ? <span className="text-[color:var(--color-primary)]">{file.name}</span> : 'Cliquez pour choisir un fichier CSV'}
            </p>
            {preview.length > 0 && (
              <p className="text-zinc-500 text-xs mt-1">{preview.length} lignes prêtes à importer</p>
            )}
          </label>

          {/* Preview */}
          {preview.length > 0 && (
            <div className="border border-white/10">
              <p className="text-zinc-500 text-xs uppercase tracking-wider px-3 py-2 border-b border-white/10">
                Aperçu (max 10 lignes)
              </p>
              <table className="w-full text-sm">
                <thead className="text-zinc-500 text-xs">
                  <tr>
                    <th className="text-left px-3 py-1.5">Identifiant</th>
                    <th className="text-right px-3 py-1.5">Prix d'achat</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 10).map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-3 py-1.5 text-white font-mono text-xs">
                        {r.barcode || r.product_id}
                      </td>
                      <td className="px-3 py-1.5 text-right text-[color:var(--color-primary)]">
                        {r.cost_price.toFixed(2)}€
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Result summary */}
          {result && (
            <div className="space-y-2">
              <div className="bg-green-500/5 border border-green-500/30 p-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-green-400 text-sm">
                  {result.updated} produit{result.updated > 1 ? 's' : ''} mis à jour sur {result.total}
                </span>
              </div>
              {result.skipped?.length > 0 && (
                <div className="bg-amber-500/5 border border-amber-500/30 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <span className="text-amber-400 text-sm">
                      {result.skipped.length} ligne{result.skipped.length > 1 ? 's' : ''} ignorée{result.skipped.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <ul className="text-xs text-zinc-400 space-y-0.5 max-h-40 overflow-y-auto">
                    {result.skipped.slice(0, 20).map((s, i) => (
                      <li key={i}>
                        <code className="text-zinc-500">{s.row.barcode || s.row.product_id || '?'}</code> — {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-white/10 p-6 flex gap-3">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-white/10 text-zinc-400 hover:border-white/20 text-sm"
            data-testid="cost-import-cancel">
            Fermer
          </button>
          <button type="button" onClick={submit}
            disabled={!preview.length || submitting}
            className="flex-1 flex items-center justify-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2.5 hover:bg-[color:var(--color-primary-hover)] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cost-import-submit">
            <Upload className="w-4 h-4" />
            {submitting ? 'Import…' : `Importer ${preview.length || ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};
